import { createEmptyWeek, type DayOfWeek, type WeekPlan } from "@/types/meal-plan-state";
import type { SaveKind, SaveWeekResult, WeekEditor, WeekSnapshot } from "./api";
import { rebaseWeek, weeksEqual } from "./week-sync";
import { EMPTY_WEEK_META, type WeekMeta } from "./edit-attribution";

export type SaveStatus = "idle" | "saving" | "saved" | "error" | "forbidden";

/** The last server copy of a week: the merge base for my unsaved edits. */
interface ServerCopy {
  version: number;
  plan: WeekPlan;
  /** Its attribution (last human edit, last planner edit). */
  meta: WeekMeta;
}

interface QueuedSave {
  plan: WeekPlan;
  kind: SaveKind;
}

export interface Rebase {
  weekId: string;
  /** The server copy the unsaved edits started from. */
  base: WeekPlan;
  /** The server's newer copy they were rebased onto. */
  theirs: WeekPlan;
  conflictDays: DayOfWeek[];
  /** For conflicting days where only some meals clashed: their indices (else the whole day). */
  conflictMeals: Partial<Record<DayOfWeek, number[]>>;
  /** Who made the server copy's last edit, when known (names the other person in the toast). */
  editor: WeekEditor | null;
}

export interface WeekSaveQueueDeps {
  save: (weekId: string, plan: WeekPlan, baseVersion: number, kind: SaveKind) => Promise<SaveWeekResult>;
  onStatus: (status: SaveStatus) => void;
  /** A save hit a 409 and the queued week was rebased onto the server copy. */
  onRebase: (rebase: Rebase) => void;
  /** At least one save landed (refresh derived data such as the week list). */
  onSaved: () => void;
  /** A save or a 409 brought the week's latest attribution. */
  onMeta?: (weekId: string, meta: WeekMeta) => void;
}

export interface WeekSaveQueueOptions {
  /**
   * Save changes no person made (week creation, migration, plan adoption).
   * Off for a friend's weeks: just looking at them must write nothing, so
   * those changes wait in the diff and land with the planner's first edit.
   */
  saveSystemChanges?: boolean;
}

/** Retries after a 409 before giving up with "error" (another device keeps winning). */
export const MAX_CONFLICT_RETRIES = 3;

export class UnsavedChangesError extends Error {
  constructor(message = "Hay cambios sin guardar") {
    super(message);
    this.name = "UnsavedChangesError";
  }
}

/**
 * Versioned autosave for weeks, independent of React.
 *
 * - `load` records the server copy of a week (its version and plan) as the
 *   merge base. A week counts as dirty only while its plan differs from that
 *   base semantically (`weeksEqual`), so reloading a week never saves the
 *   server's own content back.
 * - `enqueue` keeps the latest unsaved plan per week and debounces a `drain`.
 * - `drain` sends one save at a time with the base's version. A 409 rebases
 *   the queued plan onto the server copy and retries (at most
 *   MAX_CONFLICT_RETRIES times). A thrown error or 403 stops it and keeps the
 *   queue, so a later `drain` retries.
 * - With `saveSystemChanges: false`, a queued plan with no edit in it is
 *   dropped instead of saved; it still differs from the base, so the next
 *   edit's save carries it.
 */
export class WeekSaveQueue {
  private readonly bases = new Map<string, ServerCopy>();
  private readonly queue = new Map<string, QueuedSave>();
  private running: Promise<void> | null = null;
  private inFlightWeekId: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private status: SaveStatus = "idle";
  private readonly saveSystemChanges: boolean;

  constructor(
    private readonly deps: WeekSaveQueueDeps,
    private readonly debounceMs = 300,
    options: WeekSaveQueueOptions = {},
  ) {
    this.saveSystemChanges = options.saveSystemChanges ?? true;
  }

  /**
   * With a week id: true while that week has an unsaved plan queued or a save
   * in flight. Without one: true while anything is queued, scheduled or saving.
   */
  hasPending(weekId?: string): boolean {
    if (weekId !== undefined) return this.queue.has(weekId) || this.inFlightWeekId === weekId;
    return this.queue.size > 0 || this.running !== null || this.timer !== null;
  }

  baseVersion(weekId: string): number | undefined {
    return this.bases.get(weekId)?.version;
  }

  /**
   * Record a freshly fetched server copy as the week's base. Returns the
   * stored plan to prepare for display: the snapshot's, or, when an earlier
   * save of this week is still queued, that plan rebased onto the snapshot
   * (the queue then holds the rebased plan, so nothing stale gets saved).
   */
  load(
    weekId: string,
    snapshot: Pick<WeekSnapshot, "plan" | "version"> & Partial<WeekMeta>,
  ): {
    stored: WeekPlan | null;
    conflictDays: DayOfWeek[];
    /** See `Rebase.conflictMeals`. */
    conflictMeals: Partial<Record<DayOfWeek, number[]>>;
    /** The snapshot's last editor, for naming them when `conflictDays` isn't empty. */
    editor: WeekEditor | null;
    /** The kept base's attribution (the snapshot's, unless the base was newer). */
    meta: WeekMeta;
  } {
    const previous = this.bases.get(weekId);
    // A snapshot fetched before one of my saves landed is older than the base
    // that save left behind; versions only grow, so keep the newer copy.
    const current = previous && previous.version > snapshot.version ? previous : snapshot;
    const theirs = current.plan ?? createEmptyWeek();
    const meta = current === previous ? previous.meta : metaOf(snapshot);
    this.bases.set(weekId, { version: current.version, plan: theirs, meta });

    const queued = this.queue.get(weekId);
    const editor = current === snapshot ? (snapshot.editedBy ?? null) : null;
    if (!queued) return { stored: current.plan, conflictDays: [], conflictMeals: {}, editor, meta };

    const { plan, conflictDays, conflictMeals } = rebaseWeek(previous?.plan ?? createEmptyWeek(), queued.plan, theirs);
    this.queue.set(weekId, { plan, kind: queued.kind });
    this.schedule();
    return { stored: plan, conflictDays: reportedConflicts(queued.kind, conflictDays), conflictMeals, editor, meta };
  }

  /**
   * Note the current plan of a loaded week. It is queued when it differs from
   * the server copy, or when a save of the week is already queued or in
   * flight (its newer plan supersedes that one, even if it reverts it).
   * `kind` stays "edit" once any edit is queued.
   */
  enqueue(weekId: string, plan: WeekPlan, userEdit: boolean): void {
    const base = this.bases.get(weekId);
    if (!base) return;
    const queued = this.queue.get(weekId);
    if (queued?.plan === plan && (!userEdit || queued.kind === "edit")) return;
    if (!queued && this.inFlightWeekId !== weekId && weeksEqual(plan, base.plan)) return;
    this.queue.set(weekId, { plan, kind: userEdit || queued?.kind === "edit" ? "edit" : "system" });
    this.schedule();
  }

  /** Debounced drain; failures surface through the status. */
  schedule(): void {
    this.cancelTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.drain().catch((error: unknown) => console.error("Failed to save week plan", error));
    }, this.debounceMs);
  }

  /** Forget every unsaved plan (the user chose to leave without saving). */
  discard(): void {
    this.cancelTimer();
    this.queue.clear();
  }

  cancelTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Save everything queued, one request at a time. Concurrent callers share
   * the run in progress. A week whose save fails (network/server error, 403,
   * too many conflicts) stays queued and is skipped for the rest of the run,
   * so it doesn't hold back other weeks; the run then rejects.
   */
  drain(): Promise<void> {
    this.cancelTimer();
    if (this.running) {
      // Something may be queued after the running loop's last look.
      return this.running.then(() => (this.queue.size > 0 ? this.drain() : undefined));
    }
    const run = this.run().finally(() => {
      if (this.running === run) this.running = null;
    });
    this.running = run;
    return run;
  }

  /** Save now; rejects if anything is still unsaved afterwards. */
  async flush(): Promise<void> {
    await this.drain();
    if (this.queue.size > 0) throw new UnsavedChangesError();
  }

  private setStatus(status: SaveStatus): void {
    if (status === this.status) return;
    this.status = status;
    this.deps.onStatus(status);
  }

  private async run(): Promise<void> {
    const conflicts = new Map<string, number>();
    const failed = new Set<string>();
    let firstError: unknown = null;
    let forbidden = false;
    let saved = false;
    try {
      for (;;) {
        const next = [...this.queue].find(([weekId]) => !failed.has(weekId));
        if (!next) break;
        const [weekId, entry] = next;
        const base = this.bases.get(weekId) ?? { version: 0, plan: createEmptyWeek(), meta: EMPTY_WEEK_META };
        if (weeksEqual(entry.plan, base.plan) || (entry.kind === "system" && !this.saveSystemChanges)) {
          this.queue.delete(weekId);
          continue;
        }
        const fail = (error: unknown) => {
          failed.add(weekId);
          firstError ??= error;
        };

        this.setStatus("saving");
        this.inFlightWeekId = weekId;
        let result: SaveWeekResult;
        try {
          result = await this.deps.save(weekId, entry.plan, base.version, entry.kind);
        } catch (error) {
          fail(error);
          continue;
        } finally {
          this.inFlightWeekId = null;
        }

        if (result.status === "saved") {
          const meta = metaOf(result);
          this.bases.set(weekId, { version: result.version, plan: entry.plan, meta });
          this.deps.onMeta?.(weekId, meta);
          // Newer edits may have been queued while this one was in flight.
          if (this.queue.get(weekId) === entry) this.queue.delete(weekId);
          saved = true;
          continue;
        }

        if (result.status === "forbidden") {
          forbidden = true;
          fail(new Error("No tienes permiso para guardar esta semana"));
          continue;
        }

        // 409: rebase the latest queued plan onto the server copy and retry.
        const latest = this.queue.get(weekId) ?? entry;
        const theirs = result.current.plan ?? createEmptyWeek();
        const { plan, conflictDays, conflictMeals } = rebaseWeek(base.plan, latest.plan, theirs);
        const meta = metaOf(result.current);
        this.bases.set(weekId, { version: result.current.version, plan: theirs, meta });
        this.deps.onMeta?.(weekId, meta);
        this.queue.set(weekId, { plan, kind: latest.kind });
        this.deps.onRebase({
          weekId,
          base: base.plan,
          theirs,
          conflictDays: reportedConflicts(latest.kind, conflictDays),
          conflictMeals,
          editor: result.current.editedBy,
        });

        const attempts = (conflicts.get(weekId) ?? 0) + 1;
        conflicts.set(weekId, attempts);
        if (attempts > MAX_CONFLICT_RETRIES) {
          fail(new Error("La semana cambió varias veces mientras guardábamos"));
        }
      }
    } finally {
      if (saved) this.deps.onSaved();
    }

    if (failed.size > 0) {
      this.setStatus(forbidden ? "forbidden" : "error");
      throw firstError;
    }
    if (saved) this.setStatus("saved");
    else if (this.status !== "saved") this.setStatus("idle");
  }
}

/** The attribution fields of a snapshot or save result (missing ones are null). */
function metaOf(source: Partial<WeekMeta>): WeekMeta {
  return {
    editedBy: source.editedBy ?? null,
    editedAt: source.editedAt ?? null,
    plannerEdit: source.plannerEdit ?? null,
  };
}

/**
 * Conflicting days worth telling the user about: only when my queued change
 * includes an edit. A system change (creation, adoption) that loses to
 * another device's edit is not "your change".
 */
function reportedConflicts(kind: SaveKind, conflictDays: DayOfWeek[]): DayOfWeek[] {
  return kind === "edit" ? conflictDays : [];
}
